import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetContactLists } from 'routes/screens/lists/hooks';
import { ActionButton } from 'routes/styles';
import Icon from 'shared/components/Icon';
import {
  BACK_END_FIELDS,
  CONTACT_UPDATE_SUPPORTED_FIELD_NAMES,
  CONTACT_UPDATE_SUPPORTED_FIELD_TYPES,
  CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES,
  FILTER_DATE_TIME_VALUE_FIELDS,
  FILTER_DATE_VALUE_FIELDS,
  TEMPORARY_HIDDEN_FIELDS,
  UNSUPPORTED_FIELDS_FOR_NOW,
} from 'shared/constants';
import { useGetContactForm } from 'shared/hooks';
import { FormServFieldType, WorkflowType } from 'shared/types';
import {
  getContactFieldType,
  getValueOptionsByContactField,
} from '../../utils';
import { ActionContainer } from '../../styles';
import { NUMBER_OF_ALLOWED_CONTACT_FIELDS } from '../../constants';
import Contact from './contact';

type UpdateContactProps = {
  disabled: boolean;
  actionIndex: number;
};

const UpdateContact = (props: UpdateContactProps) => {
  const { disabled, actionIndex } = props;
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<WorkflowType>();

  const event = watch('event');
  const contact_fields = watch(
    `actions.${actionIndex}.action_meta.contact_fields`,
  );
  const {
    data: contactFormData,
    isLoading: isLoadingContactForm,
    // isError: isContactFormError,
  } = useGetContactForm({ enabled: true });

  const { data: contactListsData = {}, isFetching: isLoadingContactLists } =
    useGetContactLists();

  const onAddContactField = () => {
    if ((contact_fields || []).length >= NUMBER_OF_ALLOWED_CONTACT_FIELDS)
      return;
    const newContactFields = [...(contact_fields || [])];
    newContactFields.push({ field: '', value: '' });
    setValue(
      `actions.${actionIndex}.action_meta.contact_fields`,
      newContactFields,
    );
  };

  const onDeleteContactField = (indexToDelete) => {
    if ((contact_fields || []).length <= 1) return;
    const newContactFields = [...(contact_fields || [])].filter(
      (cf, idx) => idx !== indexToDelete,
    );
    setValue(
      `actions.${actionIndex}.action_meta.contact_fields`,
      newContactFields,
    );
  };

  const contactFields = React.useMemo(
    () =>
      contactFormData?.fields
        ?.filter(
          (field: FormServFieldType) =>
            (field.visible &&
              ![...TEMPORARY_HIDDEN_FIELDS, ...BACK_END_FIELDS].includes(
                field.name,
              ) &&
              ![
                ...UNSUPPORTED_FIELDS_FOR_NOW,
                ...FILTER_DATE_TIME_VALUE_FIELDS,
                ...FILTER_DATE_VALUE_FIELDS,
              ].includes(field.type) &&
              CONTACT_UPDATE_SUPPORTED_FIELD_TYPES.includes(field.type) &&
              !CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES.includes(field.name)) ||
            CONTACT_UPDATE_SUPPORTED_FIELD_NAMES.includes(field.name),
        )
        .filter((field: FormServFieldType) =>
          event === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED'
            ? field.name !== 'is_unsubscribed'
            : true,
        )
        .map((field) => ({
          id: field.name,
          label: field.label,
          type: getContactFieldType(field),
          choices: getValueOptionsByContactField({
            field: field,
            contactLists: contactListsData?.contactList || [],
          }),
          field_type: field?.type,
        })),
    [contactFormData, contactListsData],
  );

  return (
    <ActionContainer>
      {contact_fields?.map((cf, idx) => (
        <div
          className="t-w-h-row"
          key={cf.field}
          style={{ alignItems: 'baseline' }}
        >
          <Contact
            key={cf.field}
            allContactFields={contactFields || []}
            contactField={cf}
            contactLists={contactListsData?.contactList || []}
            actionIndex={actionIndex}
            fieldIndex={idx}
            disabled={disabled || isLoadingContactForm}
          />
          <ActionButton
            onClick={() => onDeleteContactField(idx)}
            className={disabled ? 'disabled' : ''}
          >
            <div>
              <Icon width="12" height="12" glyph="minus" />
            </div>
          </ActionButton>
        </div>
      ))}
      <ActionButton
        onClick={onAddContactField}
        className={
          disabled ||
          (contact_fields &&
            contact_fields?.length >= NUMBER_OF_ALLOWED_CONTACT_FIELDS)
            ? 'disabled'
            : ''
        }
      >
        <div>
          <Icon width="12" height="12" glyph="plus" />
        </div>
        <div>{`${t('add')} ${t(
          'workflow.condition.condition_variable.contact_field',
        ).toLowerCase()}`}</div>
      </ActionButton>
    </ActionContainer>
  );
};

export default UpdateContact;
